// App shell — the KitchenOS ("Yes, Chef") frame: header, floating side panel
// with the five modes, the mode canvas, and the chef bar pinned below.
// Ported from MUD's Layout.tsx, which itself mirrors cinematic_database.
//
// Header sections, split by faded vertical dividers:
//   [ chef's hat (in the corner rail) + CHEF wordmark ] | [ centre, empty ] | [ theme, settings ]
//
// Below the header a left side panel as a floating card (8 px inset, rounded,
// border, shadow) with three states:
//   expanded — --panel-width;
//   rail     — --rail-width, the same box the logo is centred in;
//   rolled   — rolled up to its top row: a small squircle around the glyph.
// The sidebar glyph in the card's top-left row switches expanded <-> rail and
// never moves (left-aligned with an inset that centres it in the rail); rolled,
// it re-opens the panel. A half-height bar across the card's foot with a
// centred up-chevron rolls it up at its current width.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ChevronUp, Moon, Settings, Sun, SunMoon } from "lucide-react";
import { NavLink } from "react-router-dom";

import { ChefBar } from "@/components/ChefBar";
import { MODES, ModeIcon } from "@/components/mode-icons";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { isPhone } from "@/phone";
import { getPref, setPref } from "@/prefs";
import { playClick, playSliderTick, playSwitch, playThemeChime } from "@/sounds";
import {
  THEME_CYCLE,
  applyMix,
  applyMono,
  applyTouch,
  effectiveMix,
  getStoredTheme,
  setStoredTheme,
  type Theme,
} from "@/theme";

// "Chef" ↔ "Yes, Chef": the name stays put, the italic "Yes, " slides open
// in front of it (the Epicure headline flourish). Click pins it.
const WORDMARK_PREFIX = "Yes, ";
const WORDMARK_NAME = "Chef";

// Collapse hysteresis: the wordmark waits this long after the pointer leaves
// before closing, and any re-entry cancels the close. Without it a pointer
// resting on the hitbox edge sees leave/enter pairs while the width animates
// and the name stutters open and shut.
const COLLAPSE_DELAY_MS = 250;

// Side panel motion: width change, and the roll-up/down of the card.
const PANEL_WIDTH_MS = 300;
const PANEL_ROLL_MS = 500;

// Header theme button: icon and label describe the mode the next press selects,
// so the button reads as "switch to …" like the original two-state toggle.
const THEME_META: Record<Theme, { label: string; Icon: typeof Sun }> = {
  dark: { label: "Dark mode", Icon: Moon },
  light: { label: "Light mode", Icon: Sun },
  custom: { label: "Custom mode", Icon: SunMoon },
};

export function Layout({
  children,
  busy = false,
}: {
  children: ReactNode;
  /** Loops the logo glow as an "alive" indicator while MUD is working. */
  busy?: boolean;
}) {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());
  const [hairline, setHairline] = useState(() => getPref("hairline"));

  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const collapseTimer = useRef<number | null>(null);
  const expanded = pinned || hovered;

  function open() {
    if (collapseTimer.current != null) {
      window.clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setHovered(true);
  }
  function scheduleClose() {
    if (collapseTimer.current != null) window.clearTimeout(collapseTimer.current);
    collapseTimer.current = window.setTimeout(() => {
      collapseTimer.current = null;
      setHovered(false);
    }, COLLAPSE_DELAY_MS);
  }
  useEffect(() => {
    return () => {
      if (collapseTimer.current != null) window.clearTimeout(collapseTimer.current);
    };
  }, []);

  const [sounds, setSounds] = useState(() => getPref("sounds"));
  const [volume, setVolume] = useState(() => getPref("volume"));

  // Side panel — a floating card. Preferences: width mode (expanded | rail)
  // and rolled flag, kept apart so rolling down restores the previous width.
  // The foot bar rolls the card up at its current width — the rail becomes a
  // 44 × 44 squircle, the expanded panel a wide bar — and the glyph, still in
  // place at the left, rolls it back down. No separate pill.
  // On a phone the card starts as the rail, rolled up — a squircle in the
  // corner — so the canvas (usually the shopping list) gets the whole width.
  const [panelMode, setPanelMode] = useState(() => (isPhone() ? "rail" : getPref("sidepanel")));
  const [rolled, setRolled] = useState(() => getPref("sidepanelHidden") || isPhone());
  const panelExpanded = panelMode === "expanded";
  const narrow = !panelExpanded;

  function setPanelRolled(next: boolean) {
    setRolled(next);
    setPref("sidepanelHidden", next);
    playClick();
  }

  function onGlyph() {
    if (rolled) return setPanelRolled(false);
    const next = panelExpanded ? "rail" : "expanded";
    setPanelMode(next);
    setPref("sidepanel", next);
    playClick();
  }

  // Custom-mode mix (0 = dark … 1 = light); the slider edits it live and the
  // palette follows through --mix-custom. The chime plays "into light" when the
  // rendered mix ends up brighter than before, whichever way the change came.
  const [mix, setMix] = useState(() => getPref("mix"));
  const committedMix = useRef(effectiveMix(theme, mix));
  const shownMix = effectiveMix(theme, mix);
  const nextTheme = THEME_CYCLE[(THEME_CYCLE.indexOf(theme) + 1) % THEME_CYCLE.length];
  const NextThemeIcon = THEME_META[nextTheme].Icon;

  function cycleTheme() {
    const before = effectiveMix(theme, mix);
    setTheme(nextTheme);
    setStoredTheme(nextTheme);
    const after = effectiveMix(nextTheme, mix);
    committedMix.current = after;
    playThemeChime(after >= before);
  }

  function changeMix([v]: number[]) {
    const level = v / 100;
    setMix(level);
    setPref("mix", level);
    applyMix(level);
    if (theme !== "custom") {
      setTheme("custom");
      setStoredTheme("custom");
    }
  }

  function commitMix([v]: number[]) {
    const level = v / 100;
    playThemeChime(level >= committedMix.current);
    committedMix.current = level;
  }

  function toggleHairline(on: boolean) {
    setHairline(on);
    setPref("hairline", on);
    playSwitch(on);
  }

  const [mono, setMono] = useState(() => getPref("mono"));
  function toggleMono(on: boolean) {
    setMono(on);
    setPref("mono", on);
    applyMono(on);
    playSwitch(on);
  }

  const [touch, setTouch] = useState(() => getPref("touch"));
  function changeTouch(v: "auto" | "on" | "off") {
    setTouch(v);
    setPref("touch", v);
    applyTouch(v);
    playClick();
  }

  function toggleSounds(on: boolean) {
    setSounds(on);
    setPref("sounds", on);
    // Turning sounds on gets audible confirmation even though the pref was
    // off a moment ago; turning them off stays silent.
    if (on) playSwitch(true, { force: true });
  }

  function changeVolume([v]: number[]) {
    const level = v / 100;
    setVolume(level);
    setPref("volume", level);
  }

  return (
    <TooltipProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <header
          className="header-hairline flex h-[var(--header-height)] shrink-0 items-center bg-surface px-3.5"
          data-hairline={hairline ? undefined : "off"}
        >
          {/* ── left: logo mark (home) in the corner rail + suite wordmark ── */}
          <div className="flex shrink-0 items-center gap-2.5">
            {/* Rail: cancels the header padding so it starts at the window
                edge, and centres the logo in --rail-width — the width the
                side panel below takes in its rail state. */}
            <div className="-ml-3.5 flex w-[var(--rail-width)] shrink-0 items-center justify-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href="/"
                    aria-label="Back to home view"
                    className="logo-glow size-[var(--logo-size)]"
                    data-busy={busy ? "true" : undefined}
                  >
                    <img src="/logoicon.svg" alt="" className="logo-mark block size-full" />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  Back to home view
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Hover (or focus) slides the wordmark open to the full name;
                click pins it so it survives the pointer leaving. The hitbox is
                27 px tall like the icon buttons and never shrinks on hover:
                capitals keep a constant tracking, the lowercase runs carry
                the 1 px tighter tracking themselves. */}
            <button
              type="button"
              aria-label="Yes, Chef"
              aria-pressed={pinned}
              onClick={() => {
                setPinned((p) => !p);
                playClick();
              }}
              onMouseEnter={open}
              onMouseLeave={scheduleClose}
              onFocus={open}
              onBlur={scheduleClose}
              className="-mr-1.5 flex h-[var(--control)] cursor-pointer items-center whitespace-pre bg-transparent py-0 pl-0 pr-1.5 font-logo text-[length:var(--wordmark-size)] font-semibold leading-none tracking-[0.01em] text-foreground"
            >
              <span
                className={cn(
                  "grid transition-[grid-template-columns,opacity] duration-300 ease-epicure",
                  expanded ? "grid-cols-[1fr] opacity-100" : "grid-cols-[0fr] opacity-0",
                )}
                aria-hidden={!expanded}
              >
                {/* overflow-x-clip: clip the sliding width only, so the
                    comma's descender stays visible */}
                <span className="min-w-0 overflow-x-clip overflow-y-visible italic text-brand-ink">
                  {WORDMARK_PREFIX}
                </span>
              </span>
              {WORDMARK_NAME}
            </button>
          </div>

          <HeaderDivider />

          {/* ── centre: empty for now ── */}
          <div className="flex-1" />

          <HeaderDivider />

          {/* ── right: theme toggle + settings ── */}
          <div className="flex shrink-0 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-header"
                  onClick={cycleTheme}
                  aria-label={`Switch to ${THEME_META[nextTheme].label.toLowerCase()}`}
                >
                  <NextThemeIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end">
                {THEME_META[nextTheme].label}
              </TooltipContent>
            </Tooltip>

            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-header"
                      aria-label="Settings"
                      onClick={playClick}
                    >
                      <Settings />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end">
                  Settings
                </TooltipContent>
              </Tooltip>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-64 border-line-strong bg-card p-3 text-xs text-card-foreground shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
              >
                <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.08em] text-dim">
                  Theme
                </div>
                {/* Dark ↔ light mix. Any move switches to the custom mode; the
                    presets show as the slider's end positions. */}
                <div className="flex items-center gap-3">
                  <Moon aria-hidden className="size-3.5 shrink-0 text-dim" />
                  <Slider
                    id="pref-mix"
                    min={0}
                    max={100}
                    step={1}
                    value={[Math.round(shownMix * 100)]}
                    onValueChange={changeMix}
                    onValueCommit={commitMix}
                    aria-label="Theme mix, dark to light"
                  />
                  <Sun aria-hidden className="size-3.5 shrink-0 text-dim" />
                  <EditableNumber
                    value={Math.round(shownMix * 100)}
                    min={0}
                    max={100}
                    aria-label="Theme mix, percent light"
                    onCommit={(n) => {
                      changeMix([n]);
                      commitMix([n]);
                    }}
                  />
                </div>

                <div className="mt-3 mb-2 font-mono text-[11px] uppercase tracking-[0.08em] text-dim">
                  Interface
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="pref-hairline" className="cursor-pointer text-xs font-normal">
                    Header hairline
                  </Label>
                  <Switch
                    id="pref-hairline"
                    size="sm"
                    checked={hairline}
                    onCheckedChange={toggleHairline}
                  />
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-3">
                  <Label htmlFor="pref-mono" className="cursor-pointer text-xs font-normal">
                    Monochrome
                  </Label>
                  <Switch id="pref-mono" size="sm" checked={mono} onCheckedChange={toggleMono} />
                </div>
                {/* Touch sizes: auto follows the primary pointer; on/off pin it. */}
                <div className="mt-2.5 flex items-center justify-between gap-3">
                  <span className="text-xs font-normal">Touch sizes</span>
                  <div className="flex gap-1">
                    {(["auto", "on", "off"] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => changeTouch(v)}
                        aria-pressed={touch === v}
                        className={cn(
                          "cursor-pointer rounded-md px-2 py-0.5 font-mono text-[11px] transition-colors",
                          touch === v
                            ? "bg-panel text-foreground"
                            : "text-dim hover:text-foreground",
                        )}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-3 mb-2 font-mono text-[11px] uppercase tracking-[0.08em] text-dim">
                  Sound
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="pref-sounds" className="cursor-pointer text-xs font-normal">
                    Sound effects
                  </Label>
                  <Switch
                    id="pref-sounds"
                    size="sm"
                    checked={sounds}
                    onCheckedChange={toggleSounds}
                  />
                </div>
                <div className="mt-2.5 flex items-center gap-3">
                  <Label htmlFor="pref-volume" className="w-14 shrink-0 text-xs font-normal">
                    Volume
                  </Label>
                  <Slider
                    id="pref-volume"
                    min={0}
                    max={100}
                    step={5}
                    value={[Math.round(volume * 100)]}
                    onValueChange={changeVolume}
                    onValueCommit={([v]) => playSliderTick(v / 100)}
                    disabled={!sounds}
                    aria-label="Sound volume"
                  />
                  <EditableNumber
                    value={Math.round(volume * 100)}
                    min={0}
                    max={100}
                    disabled={!sounds}
                    aria-label="Sound volume, percent"
                    onCommit={(n) => {
                      changeVolume([n]);
                      playSliderTick(n / 100);
                    }}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <div className="relative flex min-h-0 flex-1">
          {/* ── left side panel ──
              A spacer holds the column open for the canvas (card width plus
              the 8 px inset each side; nothing while rolled, the squircle then
              floats over the canvas). The card itself is absolutely placed so
              it can roll up — height shrinks with the top fixed — without
              reflowing anything; overflow-hidden plus the radius clips the
              content as it rolls. Content goes between the top row and the
              foot bar later. */}
          <div
            aria-hidden
            style={{
              // card width + 2 px border + 8 px inset each side
              width: rolled ? 0 : `calc(${narrow ? "var(--rail-width)" : "var(--panel-width)"} + 18px)`,
              transition: `width ${PANEL_WIDTH_MS}ms ease-out`,
            }}
            className="shrink-0"
          />
          <aside
            id="sidepanel"
            aria-label="Side panel"
            style={{
              // Sizes are inner sizes plus the 1 px border on each side, so the
              // inner box is exactly --rail-width / --panel-width and the 8.5 px
              // inset centres a 27 px button in the rail.
              width: `calc(${narrow ? "var(--rail-width)" : "var(--panel-width)"} + 2px)`,
              // Rolled: rail square, so the glyph sits centred in a squircle.
              height: rolled ? "calc(var(--rail-width) + 2px)" : "calc(100% - 16px)",
              transition: `width ${PANEL_WIDTH_MS}ms ease-out, height ${PANEL_ROLL_MS}ms ease-in-out`,
            }}
            className="absolute top-2 left-2 z-10 flex flex-col overflow-hidden rounded-lg border border-line-strong bg-surface shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
          >
            {/* Top row is rail-height (44 px) so a 27 px button has the same
                8.5 px inset on every side — centred in the rail and in the
                rolled squircle. */}
            <div className="flex h-[var(--rail-width)] shrink-0 items-center px-[var(--inset)]">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-header"
                    onClick={onGlyph}
                    aria-expanded={!rolled}
                    aria-label={
                      rolled
                        ? "Show side panel"
                        : panelExpanded
                          ? "Collapse side panel to rail"
                          : "Expand side panel"
                    }
                  >
                    <SidebarIcon open={!narrow} className="size-[var(--glyph)]" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" align="start">
                  {rolled ? "Show side panel" : panelExpanded ? "Collapse to rail" : "Expand side panel"}
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Mode list: control-sized rows at the glyph's inset, so they
                are the rail's squares when narrow and grow a label to the
                right when expanded. The list starts under the glyph row with
                a 20 px gap. Hover colours icon and label in the mode's own
                accent (--mode-acc, see index.css); the active mode keeps its
                accent on a panel fill. */}
            <nav
              aria-label="Modes"
              // Scrollable for short windows, but without a scrollbar: one would
              // flash every time the card rolls through heights below the list.
              className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto [scrollbar-width:none]"
            >
              <div className="flex flex-col gap-1.5 px-[var(--inset)] pt-5 pb-2">
                {MODES.map((m) => (
                  <NavLink
                    key={m.id}
                    to={m.path}
                    onClick={() => playClick()}
                    aria-label={m.label}
                    tabIndex={rolled ? -1 : undefined}
                    style={{ "--mode-acc": m.accent } as CSSProperties}
                    className={({ isActive }) =>
                      cn(
                        "mode-btn flex h-[var(--control)] w-full shrink-0 cursor-pointer items-center gap-2 rounded-md bg-transparent transition-colors",
                        isActive ? "bg-panel text-[var(--acc)]" : "text-dim hover:text-[var(--acc)]",
                      )
                    }
                  >
                    <span className="flex size-[var(--control)] shrink-0 items-center justify-center">
                      <ModeIcon className="size-[var(--glyph)]">{m.icon}</ModeIcon>
                    </span>
                    <span
                      className={cn(
                        "truncate font-mono text-xs transition-opacity duration-200",
                        narrow ? "opacity-0" : "opacity-100",
                      )}
                    >
                      {m.label}
                    </span>
                  </NavLink>
                ))}
              </div>
            </nav>

            {/* Foot bar: half a header button tall, full width, chevron
                centred — so it is centred in every width state. Fades while
                the card rolls so it does not ride the bottom edge up. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setPanelRolled(true)}
                  aria-label="Hide side panel"
                  tabIndex={rolled ? -1 : undefined}
                  className={cn(
                    "mt-auto flex h-3.5 w-full shrink-0 cursor-pointer items-center justify-center bg-transparent text-dim transition-[background-color,color,opacity] duration-200 hover:bg-panel hover:text-foreground",
                    rolled && "pointer-events-none opacity-0",
                  )}
                >
                  <ChevronUp className="size-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                Hide side panel
              </TooltipContent>
            </Tooltip>
          </aside>

          <main className="flex min-h-0 min-w-0 flex-1">{children}</main>
        </div>

        {/* ── chef bar: the assistant's input line, in every mode ── */}
        <ChefBar />
      </div>
    </TooltipProvider>
  );
}

// Readout beside a slider that turns into a text field on click. Enter or blur
// commits (integers only, clamped to min..max), Escape cancels, anything that
// is not a number leaves the value untouched. Same box in both states so the
// row never shifts.
function EditableNumber({
  value,
  min,
  max,
  onCommit,
  disabled = false,
  "aria-label": ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  onCommit: (value: number) => void;
  disabled?: boolean;
  "aria-label": string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const editing = draft !== null;

  function commit() {
    if (draft === null) return;
    const n = Number.parseInt(draft, 10);
    setDraft(null);
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(max, Math.max(min, n));
    if (clamped !== value) onCommit(clamped);
  }

  const box = "w-8 shrink-0 text-right font-mono text-[11px] tabular-nums leading-[18px]";

  if (editing) {
    return (
      <input
        type="text"
        inputMode="numeric"
        autoFocus
        value={draft}
        aria-label={ariaLabel}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d-]/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") {
            // Cancel the edit only; without this the popover dismisses too.
            e.stopPropagation();
            setDraft(null);
          }
        }}
        className={cn(
          box,
          "rounded-none border-0 border-b border-brand bg-transparent p-0 text-foreground outline-none",
        )}
      />
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={`${ariaLabel}: ${value}. Click to type a value`}
      onClick={() => setDraft(String(value))}
      className={cn(
        box,
        "cursor-text bg-transparent p-0 text-dim transition-colors hover:text-foreground disabled:cursor-default disabled:opacity-50 disabled:hover:text-dim",
      )}
    >
      {value}
    </button>
  );
}

// Sidebar toggle glyph after Obsidian's: a rounded frame with a smaller filled
// bar inside, left aligned, standing for the sidebar. Expanded it is a wide
// bar, collapsed it thins to a line; the colour never changes, only the width,
// and the width animates with the panel. Drawn on lucide's 24-unit grid with
// Obsidian's 1.75 stroke. Sized by the caller: the button's default 15 px
// applies only to svgs without a size class.
function SidebarIcon({ open, className }: { open: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <rect
        x="6"
        y="6"
        height="12"
        rx="1.2"
        fill="currentColor"
        stroke="none"
        // SVG geometry is a CSS property in current browsers, so the width
        // transitions like any style.
        style={{ width: open ? 5.5 : 1.75, transition: "width 300ms ease-out" }}
      />
    </svg>
  );
}

// Vertical section divider: 1 px wide, 60 % of the header height, fading to
// transparent at both ends so it never touches the bar's top or bottom edge.
function HeaderDivider() {
  return (
    <div
      aria-hidden
      className="mx-3.5 h-[60%] w-px shrink-0 self-center bg-linear-to-b from-transparent via-line-strong to-transparent"
    />
  );
}
